import { fromProblemV2 } from '~/@entities/Problem'
import type {
  CommonResponseDerivationVo,
  CommonResponseDerivationWorkbookVo,
  GetPairAndSimilarByIdData,
  WeakConceptChipGroupCommonWorksheetRecommendedProblemResponse,
  WeakConceptChipPersonalizedWorksheetRecommendedProblemResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  GroupCommonWeakConceptChipRecommendationWireSchema,
  type PairSimilarProblems,
  PairSimilarProblemsWireSchema,
  type PersonalizedWeakConceptChipRecommendation,
  PersonalizedWeakConceptChipRecommendationSchema,
  type WeakConceptChipRecommendedProblem,
  type WorkbookDerivation,
  WorkbookDerivationWireSchema,
  type WorksheetDerivation,
  WorksheetDerivationWireSchema,
} from './schemas'

const ENTITY = 'Derivation'

// 생성 타입은 문제 목록 원소를 @JsonUnwrapped 로 펼쳐지는 ProblemDto 없이 문제 칸 id·tagTop 만으로 그리고, 쌍둥이·유사
// 응답의 개념(CurriculumConceptCacheDto)에 계산 프로퍼티(schoolType·grade)를 싣지 않는다. 스키마가 실제 모양을
// 검증하므로 입력은 생성 타입으로 받되 이 한 줄에서만 unknown 을 거친다.
const asWire = (
  raw:
    | CommonResponseDerivationVo['data']
    | CommonResponseDerivationWorkbookVo['data']
    | GetPairAndSimilarByIdData,
): unknown => raw

export const adaptWorksheetDerivation = (
  raw: CommonResponseDerivationVo['data'],
): WorksheetDerivation => {
  const derivation = parseApiResponse(WorksheetDerivationWireSchema, asWire(raw), ENTITY)
  return {
    ...derivation,
    problemList: (derivation?.problemList ?? []).map(
      ({ worksheetProblemId, tagTop, ...problem }) => ({
        ...fromProblemV2(problem),
        worksheetProblemId,
        tagTop,
      }),
    ),
  }
}

export const adaptWorkbookDerivation = (
  raw: CommonResponseDerivationWorkbookVo['data'],
): WorkbookDerivation => {
  const derivation = parseApiResponse(WorkbookDerivationWireSchema, asWire(raw), ENTITY)
  return {
    ...derivation,
    problemList: (derivation?.problemList ?? []).map(
      ({ workbookProblemId, tagTop, ...problem }) => ({
        ...fromProblemV2(problem),
        workbookProblemId,
        tagTop,
      }),
    ),
  }
}

export const adaptPairSimilarProblems = (raw: GetPairAndSimilarByIdData): PairSimilarProblems => {
  const result = parseApiResponse(PairSimilarProblemsWireSchema, asWire(raw), ENTITY)
  const toCandidate = ({ problem, ...rest }: (typeof result)['pairProblemList'][number]) => ({
    ...rest,
    problem: fromProblemV2(problem),
  })
  return {
    ...result,
    problem: fromProblemV2(result.problem),
    pairProblemList: (result.pairProblemList ?? []).map(toCandidate),
    similarProblemList: (result.similarProblemList ?? []).map(toCandidate),
  }
}

// 생성 타입은 RecommendedProblemResponse.tagTop 을 optional 로 그리지만 서버는 `String?` 를 null 로 낸다.
// 스키마가 실제 와이어를 검증하므로 입력을 이 한 줄에서만 unknown 으로 넘긴다.
const asRecommendationWire = (
  raw:
    | WeakConceptChipGroupCommonWorksheetRecommendedProblemResponse
    | WeakConceptChipPersonalizedWorksheetRecommendedProblemResponse,
): unknown => raw

export const adaptGroupCommonWeakConceptChipRecommendation = (
  raw: WeakConceptChipGroupCommonWorksheetRecommendedProblemResponse,
): WeakConceptChipRecommendedProblem[] =>
  (
    parseApiResponse(
      GroupCommonWeakConceptChipRecommendationWireSchema,
      asRecommendationWire(raw),
      ENTITY,
    )?.problemsWithTagTop ?? []
  ).map(({ problem, tagTop }) => ({ ...fromProblemV2(problem), tagTop }))

/** 화면이 해석하지 않고 생성 요청에 되돌려 보낼 원본이라 모양을 바꾸지 않는다 */
export const adaptPersonalizedWeakConceptChipRecommendation = (
  raw: WeakConceptChipPersonalizedWorksheetRecommendedProblemResponse,
): PersonalizedWeakConceptChipRecommendation =>
  parseApiResponse(
    PersonalizedWeakConceptChipRecommendationSchema,
    asRecommendationWire(raw),
    ENTITY,
  )
